import axiosInstance from '../utils/axiosInstance';

export interface ReadLaterItem {
    id: number;
    user_id: number;
    article_id: number;
    cluster_id?: number;
    added_at: string;
    is_read: boolean;
    read_at?: string;
    title?: string;
    headline?: string;
    summary?: string;
    image_url?: string;
    publication_date?: string;
    website_name?: string;
}

export interface ReadLaterResponse {
    items: ReadLaterItem[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

export interface AddToReadLaterRequest {
    article_id: number;
    cluster_id?: number;
}

export interface MarkAsReadRequest {
    is_read: boolean;
}

const ReadLaterService = {
    getReadLaterItems: async (page: number, pageSize: number): Promise<ReadLaterResponse> => {
        try {
            const response = await axiosInstance.get('/read-later', {
                params: { page, pageSize },
            });
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch read later items');
        }
    },

    addToReadLater: async (request: AddToReadLaterRequest): Promise<any> => {
        try {
            const response = await axiosInstance.post('/read-later', request);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to add article to read later');
        }
    },

    markAsRead: async (articleId: number, request: MarkAsReadRequest): Promise<any> => {
        try {
            const response = await axiosInstance.put(`/read-later/${articleId}/mark-read`, request);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to mark article as read');
        }
    },

    removeFromReadLater: async (articleId: number): Promise<any> => {
        try {
            const response = await axiosInstance.delete(`/read-later/${articleId}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to remove article from read later');
        }
    },

    checkReadLaterStatus: async (articleId: number): Promise<{ is_in_read_later: boolean }> => {
        try {
            const response = await axiosInstance.get(`/read-later/check/${articleId}`);
            return response.data.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to check read later status');
        }
    },

    getReadLaterCount: async (): Promise<{ count: number }> => {
        try {
            const response = await axiosInstance.get('/read-later/count');
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to get read later count');
        }
    },

    checkMultipleReadLaterStatus: async (articleIds: number[]): Promise<{ [articleId: number]: boolean }> => {
        try {
            const response = await axiosInstance.post('/read-later/check-multiple', { articleIds });
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to check read later status for multiple articles');
        }
    },
};

export default ReadLaterService; 